<script setup>
import TvClubLogo from '~/components/tv/TvClubLogo.vue';
import {tvClock, tvLongDate} from '~/utils/tvDisplay';

const props = defineProps({
  clubName: {type: String, required: true},
  logo: {type: String, default: null},
  liveCount: {type: Number, default: 0},
  connected: {type: Boolean, default: true},
  // The bar's background is dark: use the Tap Leagues logo made for dark backgrounds
  darkBar: {type: Boolean, default: false},
});

// Set in the browser only: the server's locale and timezone aren't the screen's
const now = ref(null);
let timer = null;
onMounted(() => {
  now.value = new Date();
  timer = setInterval(() => { now.value = new Date(); }, 1000);
});
onUnmounted(() => clearInterval(timer));

</script>

<template>
  <header class="tv-top">
    <div class="tv-club">
      <tv-club-logo :filename="logo" :club-name="clubName" :size="60"/>
      <span class="tv-club-name">{{ clubName }}</span>
    </div>
    <div class="tv-top-right">
      <div class="tv-powered">
        <span class="tv-powered-label">Powered by</span>
        <img :src="darkBar ? '/logo_white_red.png' : '/images/logo.png'" alt="Tap Leagues" class="tv-powered-logo">
      </div>
      <transition name="tv-pop">
        <div v-if="liveCount > 0" class="tv-live-bug">
          <span class="tv-live-dot"/>
          LIVE
          <span v-if="liveCount > 1" class="tv-live-count">{{ liveCount }}</span>
        </div>
      </transition>
      <div v-if="now" class="tv-time">
        <span class="tv-clock">{{ tvClock(now) }}</span>
        <span class="tv-date">{{ tvLongDate(now) }}</span>
      </div>
      <span v-if="!connected" class="tv-offline">
        <v-icon icon="mdi-wifi-off" size="26"/>
      </span>
    </div>
  </header>
</template>

<style scoped lang="scss">
.tv-top {
  height: 96px;
  padding: 0 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--tv-panel);
  border-bottom: 1px solid var(--tv-line);
}

.tv-club {
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
}

.tv-club-name {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 34px;
  letter-spacing: 0.2px;
  color: var(--tv-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-top-right {
  display: flex;
  align-items: center;
  gap: 40px;
}

.tv-live-bug {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 18px 6px 14px;
  border-radius: 6px;
  background: #e0243c;
  color: #fff;
  font-family: var(--tv-display);
  font-weight: 800;
  font-size: 28px;
  letter-spacing: 2px;
}

.tv-live-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  animation: tv-pulse 1.4s ease-in-out infinite;
}

.tv-live-count {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 4px;
  padding: 0 10px;
  letter-spacing: 0;
}

.tv-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1;
}

.tv-clock {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 48px;
  color: var(--tv-ink);
  font-variant-numeric: tabular-nums;
}

.tv-date {
  margin-top: 4px;
  font-size: 19px;
  color: var(--tv-muted);
}

.tv-offline {
  color: var(--tv-muted);
}

@keyframes tv-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.8); }
}

.tv-pop-enter-active { transition: transform 0.4s cubic-bezier(.2, 1.4, .4, 1), opacity 0.3s; }
.tv-pop-leave-active { transition: opacity 0.3s; }
.tv-pop-enter-from { transform: scale(0.6); opacity: 0; }
.tv-pop-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .tv-live-dot { animation: none; }
}

:global(.tv-portrait .tv-top) {
  padding: 0 48px;
}

:global(.tv-portrait .tv-club-name) {
  font-size: 33px;
}

:global(.tv-portrait .tv-date) {
  display: none;
}
.tv-powered {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 32px;
  border-right: 1px solid var(--tv-line);
}

.tv-powered-label {
  font-size: 18px;
  color: var(--tv-muted);
  white-space: nowrap;
}

.tv-powered-logo {
  display: block;
  height: 30px;
  width: auto;
}
</style>
